/**
 * Botoes "Add" da ZEPH: cross-sell do carrinho, shop the look e add-ons da PDP.
 *
 * Segue o mesmo contrato do formulario de produto do Horizon
 * (assets/product-form.js): dispara CartLinesUpdateEvent com uma promise,
 * faz o POST pedindo as secoes do carrinho e resolve com o carrinho e o HTML
 * novos. O proprio Horizon entao re-renderiza o drawer e atualiza o contador
 * do header. Nada de redraw manual nem de rebind de listeners - foi isso que
 * quebrou o remover depois de um add no carrinho v8 do Minimog.
 *
 * Marcacao:
 * - [data-zeph-cart-add="<variant id>"] no botao;
 * - [data-zeph-cart-item] no container da linha/card, que pode ter um
 *   select [data-zeph-cart-variant] (opcoes com data-price) e um
 *   [data-zeph-cart-price]. Tudo e lido dentro do proprio item.
 * - [data-zeph-cart-silent]: adiciona sem abrir o drawer (acao "update" em
 *   vez de "add") e confirma no proprio botao.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_FEEDBACK_MS = 2000;

/* Preco acompanha a variante escolhida no select do item. */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-zeph-cart-variant]')) return;

  const item = select.closest('[data-zeph-cart-item]');
  const price = item?.querySelector('[data-zeph-cart-price]');
  const option = select.selectedOptions[0];
  if (price && option?.dataset.price) price.textContent = option.dataset.price;

  /* Preco comparativo riscado: some quando a variante nao tem desconto. */
  const compare = item?.querySelector('[data-zeph-cart-compare]');
  if (compare && option && 'compare' in option.dataset) {
    compare.textContent = option.dataset.compare;
    compare.hidden = !option.dataset.compare;
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-zeph-cart-add]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const item = button.closest('[data-zeph-cart-item]');
  const variantSelect = item?.querySelector('[data-zeph-cart-variant]');
  const variantId = variantSelect instanceof HTMLSelectElement ? variantSelect.value : button.dataset.zephCartAdd;
  const silent = button.hasAttribute('data-zeph-cart-silent');
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
    (component) => component.dataset.sectionId
  );

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      // "update" atualiza drawer e contador sem disparar a abertura automatica.
      action: silent ? 'update' : 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  let failed = true;

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    }).then((res) => res.json());

    const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((res) =>
      res.json()
    );

    failed = Boolean(response.status);
    if (failed) {
      button.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'zeph-cart-add',
        itemCount: 1,
        sections: response.sections,
        didError: failed,
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }

  if (silent && !failed) {
    const status = item?.closest('.zeph-addons')?.querySelector('[data-zeph-cart-status]');
    if (status) status.textContent = `${item?.querySelector('a[href]:not([tabindex])')?.textContent?.trim() ?? 'Item'} added to cart`;
    button.dataset.added = 'true';
    setTimeout(() => delete button.dataset.added, ADDED_FEEDBACK_MS);
  }
});
